import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Clock, Zap, AlertTriangle, Bot, X } from 'lucide-react';

// Countdown timer badge for trial users
export const TrialBadge = () => {
  const { trialActive, getTrialTimeFormatted } = useSubscription();
  
  if (!trialActive) return null;
  
  const time = getTrialTimeFormatted();
  if (!time) return null;
  
  const isUrgent = time.hours < 2;
  
  return (
    <Badge 
      className={`flex items-center gap-1.5 px-2 py-1 ${
        isUrgent 
          ? 'bg-red-600 text-white animate-pulse' 
          : 'bg-orange-600 text-white'
      }`}
      data-testid="trial-badge"
    >
      <Clock className="w-3 h-3" />
      <span className="font-mono text-xs">
        {time.hours.toString().padStart(2, '0')}:
        {time.minutes.toString().padStart(2, '0')}:
        {time.seconds.toString().padStart(2, '0')}
      </span>
    </Badge>
  );
};

// Full countdown display for dashboard
export const TrialCountdownBanner = () => {
  const { trialActive, getTrialTimeFormatted } = useSubscription();
  
  if (!trialActive) return null;
  
  const time = getTrialTimeFormatted();
  if (!time) return null;
  
  const isUrgent = time.hours < 4;
  const isCritical = time.hours < 1;
  
  return (
    <div 
      className={`px-4 py-2 flex items-center justify-between ${
        isCritical 
          ? 'bg-red-900/80 border-red-700' 
          : isUrgent 
            ? 'bg-orange-900/80 border-orange-700'
            : 'bg-slate-800/80 border-slate-700'
      } border-b`}
      data-testid="trial-countdown-banner"
    >
      <div className="flex items-center gap-3">
        {isCritical ? (
          <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />
        ) : (
          <Clock className={`w-5 h-5 ${isUrgent ? 'text-orange-400' : 'text-teal-400'}`} />
        )}
        <div>
          <span className="text-sm font-medium text-white">
            24-Hour Tactical Preview Active
          </span>
          <span className="text-slate-400 text-sm ml-2">
            {isCritical 
              ? "Final hour! Don't lose access!" 
              : isUrgent 
                ? "Clock's ticking, soldier!" 
                : "Experience Tank's Command Center"}
          </span>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 bg-slate-900/50 rounded-lg px-3 py-1.5">
          <span className="text-xs text-slate-400 uppercase tracking-wide">Ends in</span>
          <span className={`font-mono text-lg font-bold ${
            isCritical ? 'text-red-400' : isUrgent ? 'text-orange-400' : 'text-white'
          }`}>
            {time.hours.toString().padStart(2, '0')}:
            {time.minutes.toString().padStart(2, '0')}:
            {time.seconds.toString().padStart(2, '0')}
          </span>
        </div>
        
        <Link to="/pricing">
          <Button 
            size="sm" 
            className={`${
              isCritical 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-teal-600 hover:bg-teal-700'
            }`}
          >
            <Zap className="w-4 h-4 mr-1" />
            Upgrade Now
          </Button>
        </Link>
      </div>
    </div>
  );
};

// Urgent trial warning modal - shows at 1 hour mark
export const TrialWarningModal = () => {
  const { trialActive, trialWarning, getTrialTimeFormatted } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  const time = getTrialTimeFormatted();
  const isFinalHour = time && time.hours < 1;
  
  // Show modal at 1-hour mark (only once per session)
  useEffect(() => {
    if (trialActive && isFinalHour && !dismissed && trialWarning?.urgency === 'critical') {
      setIsOpen(true);
    }
  }, [trialActive, isFinalHour, dismissed, trialWarning]);
  
  const handleDismiss = () => {
    setIsOpen(false);
    setDismissed(true);
  };
  
  if (!trialActive || !time) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="bg-slate-900 border-red-700 text-white sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="p-4 bg-red-500/20 rounded-full animate-pulse">
                <AlertTriangle className="w-12 h-12 text-red-400" />
              </div>
              <div className="absolute -top-2 -right-2 bg-red-600 rounded-full px-2 py-1">
                <span className="font-mono text-sm font-bold">
                  {time.minutes}:{time.seconds.toString().padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>
          <DialogTitle className="text-center text-2xl text-red-400">
            Time's Almost Up!
          </DialogTitle>
          <DialogDescription className="text-center text-slate-300">
            Your 24-Hour Tactical Preview ends in less than an hour.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Tank's message */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-teal-500/20 rounded-full">
                <Bot className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <p className="text-teal-400 font-medium mb-1">Tank says:</p>
                <p className="text-slate-300 text-sm italic">
                  "This is it, soldier! Last chance to lock in your access. 
                  The clock's almost run out, but you've seen what we can do together. 
                  Don't let this intel slip away."
                </p>
              </div>
            </div>
          </div>
          
          {/* What you'll lose */}
          <div className="bg-red-950/30 rounded-lg p-4 border border-red-900/50">
            <p className="text-sm text-red-300 mb-2 font-medium">You're about to lose access to:</p>
            <ul className="space-y-1 text-sm text-slate-400">
              <li>• Tank & Daisy AI assistants</li>
              <li>• Daily intelligence briefings</li>
              <li>• All 50 states frequencies</li>
              <li>• Threat analysis & predictions</li>
              <li>• Voice alerts & commands</li>
            </ul>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col gap-2">
            <Link to="/pricing" className="w-full" onClick={handleDismiss}>
              <Button className="w-full bg-teal-600 hover:bg-teal-700 text-lg py-6">
                <Zap className="w-5 h-5 mr-2" />
                Keep My Access - $10/month
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              onClick={handleDismiss} 
              className="w-full text-slate-400 hover:text-slate-300"
            >
              Remind me later
            </Button>
          </div>
          
          <p className="text-xs text-center text-slate-500">
            Or save 33% with annual ($80/year) • Lifetime access $199
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Start trial button component
export const StartTrialButton = ({ 
  variant = 'default', 
  size = 'default',
  className = '',
  onSuccess 
}) => {
  const { startTrial, canStartTrial } = useSubscription();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  
  if (!canStartTrial()) return null;
  
  const handleStartTrial = async () => {
    setLoading(true);
    setError(null);
    
    const result = await startTrial();
    
    setLoading(false);
    
    if (result.success) {
      onSuccess?.(result.data);
    } else {
      setError(result.message);
    }
  };
  
  return (
    <div className="space-y-2">
      <Button
        variant={variant}
        size={size}
        onClick={handleStartTrial}
        disabled={loading}
        className={`bg-orange-600 hover:bg-orange-700 text-white font-bold ${className}`}
        data-testid="start-trial-btn"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin">⏳</span>
            Activating...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            24-Hour Tactical Preview - GO!
          </span>
        )}
      </Button>
      {error && (
        <p className="text-red-400 text-sm text-center">{error}</p>
      )}
    </div>
  );
};

// Compact start trial CTA
export const TrialCTA = ({ className = '' }) => {
  const { canStartTrial, trialActive } = useSubscription();
  
  if (trialActive || !canStartTrial()) return null;
  
  return (
    <div className={`bg-gradient-to-r from-orange-900/50 to-slate-900/50 rounded-lg p-4 border border-orange-700/50 ${className}`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h4 className="text-white font-semibold text-sm">
            24-Hour Tactical Preview
          </h4>
          <p className="text-orange-200/80 text-xs">
            Experience Tank's Command Center on your first mission
          </p>
        </div>
        <StartTrialButton size="sm" />
      </div>
    </div>
  );
};

export default TrialCountdownBanner;
